import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Link2, AlertTriangle, Trash2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { TIPOS_MODALIDAD } from "@/lib/constants";

export default function BokunMapeo() {
  const [mappings, setMappings] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get("/bokun/mappings"),
        api.get("/tours", { params: { activo: true } }),
      ]);
      setMappings(m.data);
      setTours(t.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setDraft = (id, patch) =>
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));

  const valueOf = (m, field) => (drafts[m.id]?.[field] ?? m[field]) || "";

  const fmtStartTimes = (obj) =>
    Object.entries(obj || {}).map(([h, id]) => `${h}=${id}`).join(", ");

  const parseStartTimes = (str) => {
    const out = {};
    (str || "").split(",").forEach((part) => {
      const m = part.trim().match(/^(\d{1,2}:\d{2})\s*=\s*(\d+)$/);
      if (m) out[m[1]] = parseInt(m[2], 10);
    });
    return out;
  };

  const stValue = (m) => (drafts[m.id]?._st ?? fmtStartTimes(m.start_times));

  const guardar = async (m) => {
    const draft = drafts[m.id];
    if (!draft) return;
    const payload = { ...draft };
    if ("_st" in payload) {
      payload.start_times = parseStartTimes(payload._st);
      delete payload._st;
    }
    try {
      await api.put(`/bokun/mappings/${m.id}`, payload);
      toast.success("Mapeo guardado.");
      setDrafts((d) => {
        const c = { ...d };
        delete c[m.id];
        return c;
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const eliminar = async (m) => {
    try {
      await api.delete(`/bokun/mappings/${m.id}`);
      toast.success("Mapeo eliminado.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const pendientes = mappings.filter((m) => !m.tour_id).length;

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-slate-50 border-slate-200">
        <p className="text-sm text-slate-600">
          Productos de Bokun y el tour del catálogo al que corresponden. El sistema mapea automáticamente por nombre
          cuando puede; aquí podés corregir o completar los casos sueltos. Los productos sin tour asignado quedan
          marcados y sus reservas van a la <strong>Bandeja a revisar</strong> hasta que los mapees.
          {pendientes > 0 && (
            <span className="ml-1 inline-flex items-center gap-1 text-amber-700 font-medium">
              <AlertTriangle className="h-3.5 w-3.5" /> {pendientes} producto(s) sin mapear.
            </span>
          )}
        </p>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["Producto Bokun", "ID", "Tour del catálogo", "Modalidad", "Tipo", "Horarios Bokun (HH:MM=startTimeId)", ""].map((h, i) => (
                  <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-slate-400 text-sm">
                    Todavía no hay productos de Bokun. Aparecerán al sincronizar o importar reservas.
                  </TableCell>
                </TableRow>
              ) : (
                mappings.map((m) => {
                  const dirty = !!drafts[m.id];
                  return (
                    <TableRow key={m.id} data-testid={`bokun-mapping-${m.id}`} className={!m.tour_id ? "bg-amber-50/40" : ""}>
                      <TableCell className="font-medium text-slate-800 max-w-[200px] truncate">{m.bokun_product_title || "—"}</TableCell>
                      <TableCell className="font-mono text-[11px] text-slate-500">{m.bokun_product_id}</TableCell>
                      <TableCell>
                        <Select value={valueOf(m, "tour_id")} onValueChange={(v) => setDraft(m.id, { tour_id: v })}>
                          <SelectTrigger className="h-8 w-52 text-xs" data-testid={`bokun-mapping-tour-${m.id}`}>
                            <SelectValue placeholder="Sin asignar" />
                          </SelectTrigger>
                          <SelectContent>
                            {tours.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select value={valueOf(m, "modalidad") || "regular"} onValueChange={(v) => setDraft(m.id, { modalidad: v })}>
                          <SelectTrigger className="h-8 w-28 text-xs" data-testid={`bokun-mapping-modalidad-${m.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(TIPOS_MODALIDAD).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select value={valueOf(m, "tipo") || "regular"} onValueChange={(v) => setDraft(m.id, { tipo: v })}>
                          <SelectTrigger className="h-8 w-28 text-xs" data-testid={`bokun-mapping-tipo-${m.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="regular">Regular</SelectItem>
                            <SelectItem value="privado">Privado</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={stValue(m)}
                          onChange={(e) => setDraft(m.id, { _st: e.target.value })}
                          placeholder="09:00=555, 14:00=556"
                          className="h-8 w-56 text-xs"
                          data-testid={`bokun-mapping-starttimes-${m.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => guardar(m)} disabled={!dirty} data-testid={`bokun-mapping-save-${m.id}`} className="gap-1 text-slate-600">
                          <Save className="h-4 w-4" /> Guardar
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => eliminar(m)} data-testid={`bokun-mapping-delete-${m.id}`} className="h-8 w-8 text-slate-500 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
